import React from 'react';
import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Divider,
  makeStyles,
  shorthands,
  tokens,
  useId,
} from '@fluentui/react-components';
import type { AssignedRoleGroup } from './AssignedRoleGroup';

const useStyles = makeStyles({
  card: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  header: {
    ...shorthands.margin(0),
  },
  headerButton: {
    alignItems: 'flex-start',
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    borderRadius: tokens.borderRadiusMedium,
  },
  expandIcon: {
    boxSizing: 'content-box',
    width: tokens.spacingHorizontalXL,
    flexShrink: 0,
    paddingRight: tokens.spacingHorizontalS,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  roleName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: 'anywhere',
  },
  summary: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    overflowWrap: 'anywhere',
  },
  panel: {
    ...shorthands.margin(0),
    ...shorthands.padding(0, tokens.spacingHorizontalL),
    paddingLeft: `calc(${tokens.spacingHorizontalL} + ${tokens.spacingHorizontalXL} + ${tokens.spacingHorizontalS})`,
  },
  scopeContent: {
    ...shorthands.padding(tokens.spacingVerticalL, 0),
  },
  scopeTitle: {
    marginTop: 0,
    marginBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  description: {
    ...shorthands.margin(0),
    color: tokens.colorNeutralForeground2,
  },
  companies: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.margin(tokens.spacingVerticalS, 0, 0),
    ...shorthands.padding(0),
    listStyleType: 'none',
  },
});

interface AssignedRoleCardProps {
  role: AssignedRoleGroup;
}

const AssignedRoleCard = ({ role }: AssignedRoleCardProps): JSX.Element => {
  const styles = useStyles();
  const summaryId = useId('role-scope-summary');
  const hasGlobalAssignment = role.assignments.some(
    (assignment) => assignment.groupIdentifier === 'GLOBAL',
  );

  const companyGroupAssignments = role.assignments.filter(
    (assignment) => assignment.groupIdentifier === 'COMPANYGROUP',
  );
  const hasCompanyGroupAssignment = companyGroupAssignments.length > 0;

  const companyAssignments = role.assignments.filter(
    (assignment) => assignment.groupIdentifier === 'COMPANY',
  );
  const companyCodeCount = new Set(companyAssignments.map((assignment) => assignment.companyCode))
    .size;
  const scopeParts: string[] = [];

  if (hasGlobalAssignment) {
    scopeParts.push('Global scope');
  }

  if (companyCodeCount > 0) {
    scopeParts.push(`${companyCodeCount} company code${companyCodeCount === 1 ? '' : 's'}`);
  }

  if (hasCompanyGroupAssignment) {
    scopeParts.push('company-group scope');
  }

  let scopeSummary = 'No assignments';

  if (scopeParts.length > 2) {
    scopeSummary = `${scopeParts.slice(0, -1).join(', ')}, and ${
      scopeParts[scopeParts.length - 1]
    }`;
  } else if (scopeParts.length > 0) {
    scopeSummary = scopeParts.join(' and ');
  }

  return (
    <AccordionItem value={role.roleId} className={styles.card}>
      <AccordionHeader
        as="h3"
        aria-label={role.roleName}
        className={styles.header}
        expandIconPosition="start"
        expandIcon={{ className: styles.expandIcon }}
        button={{
          className: styles.headerButton,
          'aria-label': role.roleName,
          'aria-describedby': summaryId,
        }}
      >
        <span className={styles.headerText}>
          <span className={styles.roleName}>{role.roleName}</span>
          <span id={summaryId} className={styles.summary}>
            {scopeSummary}
          </span>
        </span>
      </AccordionHeader>
      <AccordionPanel className={styles.panel}>
        {hasGlobalAssignment && (
          <section aria-label="Global assignment">
            <Divider />
            <div className={styles.scopeContent}>
              <h4 className={styles.scopeTitle}>Global assignment</h4>
              <p className={styles.description}>
                Recorded at global scope. No individual company codes are stored for this
                assignment.
              </p>
            </div>
          </section>
        )}
        {companyAssignments.length > 0 && (
          <section aria-label="Company assignments">
            <Divider />
            <div className={styles.scopeContent}>
              <h4 className={styles.scopeTitle}>Company assignments</h4>
              <ul className={styles.companies}>
                {companyAssignments.map((assignment) => (
                  <li key={assignment.id}>
                    <Badge appearance="outline" shape="rounded">
                      {assignment.companyCode}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
        {hasCompanyGroupAssignment && (
          <section aria-label="Company-group assignments">
            <Divider />
            <div className={styles.scopeContent}>
              <h4 className={styles.scopeTitle}>Company-group assignments</h4>
              <p className={styles.description}>Access recorded through a company group.</p>
              <ul className={styles.companies}>
                {companyGroupAssignments.map((assignment) => (
                  <li key={assignment.id}>
                    <Badge appearance="outline" shape="rounded">
                      {assignment.companyCode}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};

export default AssignedRoleCard;
